import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, Search, Filter, Columns, Plus, GripVertical } from 'lucide-react';
import SurveyItemCard from './SurveyItemCard';

/**
 * Grid component for displaying and selecting survey items
 * @param {Object} props - Component props
 * @param {Array} props.items - Survey items to display
 * @param {string} props.selectedItemId - ID of the selected item
 * @param {Function} props.setSelectedItemId - Function to set selected item
 * @param {Function} props.toggleItemVisibility - Function to toggle item visibility
 * @param {boolean} props.showHidden - Whether to show hidden items
 * @param {Function} props.setShowHidden - Function to toggle showing hidden items
 * @param {number} props.maxColumns - Maximum number of columns to display
 * @param {Function} props.reorderItems - Function to reorder items
 * @param {boolean} props.isDragging - Whether an item is being dragged
 * @param {Function} props.setIsDragging - Function to set dragging state
 */
const SurveyItemGrid = ({ 
  items, 
  selectedItemId, 
  setSelectedItemId, 
  toggleItemVisibility, 
  showHidden, 
  setShowHidden,
  maxColumns = 5,
  reorderItems,
  isDragging,
  setIsDragging,
  cardSize = 'medium',
  setCardSize
}) => {
  const [gridColumns, setGridColumns] = useState(3);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // { index, position: 'before' | 'after' }
  const dragItemRef = useRef(null);
  const dragPreviewRef = useRef(null);
  const dragStartPositionRef = useRef({ x: 0, y: 0 });
  const dragCurrentPositionRef = useRef({ x: 0, y: 0 });
  const gridContainerRef = useRef(null);
  const itemRefs = useRef({});
  
  // Track selection source to prevent jumpiness
  const isSelectingInGridRef = useRef(false);
  
  // Track when the grid selection was last updated from the detail view
  const lastGridScrollTimeRef = useRef(Date.now());
  
  // Auto-scroll to selected item in grid only when selection changes from detail view
  useEffect(() => {
    // Only auto-scroll if there's a selected item and the ref exists
    if (selectedItemId && itemRefs.current[selectedItemId]) {
      // If we're selecting from the grid, don't need to auto-scroll
      if (isSelectingInGridRef.current) {
        isSelectingInGridRef.current = false; 
        return;
      }
      
      // If we've scrolled recently, don't cause jumpiness
      const now = Date.now();
      if (now - lastGridScrollTimeRef.current < 500) {
        return;
      }
      
      // Scroll the item into view with minimal animation
      itemRefs.current[selectedItemId].scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest'
      });
      
      // Update the last scroll time
      lastGridScrollTimeRef.current = now;
    }
  }, [selectedItemId]);
  
  // Function to update the ghost preview position
  const updateGhostPosition = (x, y) => {
    if (dragPreviewRef.current) {
      // Use the offset to position the element where the cursor grabbed it
      const offsetX = dragStartPositionRef.current.offsetX || 20;
      const offsetY = dragStartPositionRef.current.offsetY || 20;
      
      // Apply position with a subtle transition for smoother movement
      dragPreviewRef.current.style.transform = 'rotate(2deg) scale(0.95)';
      dragPreviewRef.current.style.top = `${y - offsetY}px`;
      dragPreviewRef.current.style.left = `${x - offsetX}px`;
    }
  };
  
  const handleMouseMove = (e) => {
    updateGhostPosition(e.clientX, e.clientY);
  };
  
  const handleDragStart = (e, index, element) => {
    // Calculate where the mouse is relative to the element being dragged
    const rect = element.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    dragStartPositionRef.current = { x: e.clientX, y: e.clientY, offsetX, offsetY };
    dragCurrentPositionRef.current = { x: e.clientX, y: e.clientY };
    
    // Set dataTransfer for compatibility
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    
    // Create invisible drag image to hide the browser's default drag ghost
    const invisibleEl = document.createElement('div');
    invisibleEl.style.position = 'absolute';
    invisibleEl.style.width = '0';
    invisibleEl.style.height = '0';
    invisibleEl.style.opacity = '0';
    document.body.appendChild(invisibleEl);
    e.dataTransfer.setDragImage(invisibleEl, 0, 0);
    setTimeout(() => document.body.removeChild(invisibleEl), 0);
    
    // Store the original item element for reference
    dragItemRef.current = element;
    
    // Create a preview element (clone of the original)
    const preview = element.cloneNode(true);
    preview.style.position = 'fixed';
    preview.style.top = `${e.clientY - offsetY}px`;
    preview.style.left = `${e.clientX - offsetX}px`;
    preview.style.width = `${rect.width}px`;
    preview.style.height = `${rect.height}px`;
    preview.style.transform = 'rotate(2deg) scale(0.95)';
    preview.style.pointerEvents = 'none';
    preview.style.zIndex = '1000';
    preview.style.opacity = '0.9';
    preview.style.boxShadow = '0 8px 15px rgba(0,0,0,0.25)';
    preview.style.transition = 'none'; // Remove any transitions
    document.body.appendChild(preview);
    dragPreviewRef.current = preview;
    
    // Set state to track dragging
    setDraggedItemIndex(index);
    setIsDragging(true);
    
    // Add class to indicate dragging
    element.classList.add('dragging');
    
    // Hide original element while dragging
    setTimeout(() => {
      if (element && element.style) {
        element.style.opacity = '0.3';
      }
    }, 0);
    
    // Add mousemove event listener to track mouse position and update the preview
    document.addEventListener('mousemove', handleMouseMove);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Update ghost preview position
    updateGhostPosition(e.clientX, e.clientY);
  };
  
  const handleDragEnter = (e, index) => {
    e.preventDefault();
    
    // Don't show drop indicator for the item being dragged
    if (index === draggedItemIndex) {
      setDropPosition(null);
      return;
    }
    
    // Determine if we should drop before or after the target
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    
    // Determine the center of the item
    const centerX = rect.left + rect.width / 2;
    
    // For horizontal layouts, determine left or right insertion
    // If mouse is to the left of center, insert before, otherwise after
    const position = mouseX < centerX ? 'before' : 'after';
    
    setDropPosition({ index, position });
  };
  
  const handleDragLeave = (e, index) => {
    // Check if we're leaving the current drop target
    if (dropPosition && dropPosition.index === index) {
      // We may be entering a child element, so check if we're still within the parent
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      // Only clear if we've actually left the element
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDropPosition(null);
      }
    }
  };
  
  const handleDragEnd = () => {
    // Remove the mousemove event listener
    document.removeEventListener('mousemove', handleMouseMove);
    
    // Clean up preview element
    if (dragPreviewRef.current && dragPreviewRef.current.parentNode) {
      dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
      dragPreviewRef.current = null;
    }
    
    // Restore original element
    if (dragItemRef.current) {
      dragItemRef.current.style.opacity = '1';
      dragItemRef.current.classList.remove('dragging');
      dragItemRef.current = null;
    }
    
    // Reset state
    setDraggedItemIndex(null);
    setDropPosition(null);
    setIsDragging(false);
  };
  
  const handleDrop = (e, index) => {
    e.preventDefault();
    
    if (!dropPosition) return;
    
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    let toIndex = dropPosition.index;
    
    // Handle edge cases with grid layout
    const currentRow = Math.floor(toIndex / gridColumns);
    const desiredPosition = dropPosition.position;
    
    if (desiredPosition === 'before') {
      // Insert before the current item
      // No adjustment needed to toIndex
    } else if (desiredPosition === 'after') {
      // Insert after the current item
      toIndex += 1;
      
      // If we're at the end of a row, make sure we don't accidentally wrap to the next row
      const isEndOfRow = (toIndex % gridColumns === 0);
      const nextItemRow = Math.floor(toIndex / gridColumns);
      
      if (isEndOfRow && nextItemRow > currentRow) {
        // We're at the end of a row, ensure we stay in the correct row
        // This prevents horizontal reordering from affecting row order
        const rowStart = currentRow * gridColumns;
        const rowEnd = rowStart + gridColumns - 1;
        toIndex = Math.min(toIndex, rowEnd + 1);
      }
    }
    
    // Adjust for item removal from original position affecting target index
    if (fromIndex < toIndex) {
      toIndex -= 1;
    }
    
    if (fromIndex !== toIndex) {
      reorderItems(fromIndex, toIndex);
    }
    
    handleDragEnd();
  };
  
  // Filter items based on search term
  const searchFilteredItems = searchTerm
    ? items.filter(item => 
        item.refId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;
  
  return (
    <div className="flex flex-col h-full max-w-full overflow-hidden">
      <div className="flex justify-between items-center mb-2 p-1 border-b pb-2">
        <h2 className="text-base font-semibold text-gray-700">Items</h2>
        <div className="flex gap-1 flex-shrink-0">
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              className="pl-6 pr-2 py-1 rounded-md border border-gray-300 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none w-20"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Search size={12} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <button 
            className={`p-1 rounded-md ${showHidden ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
            title="Toggle Hidden Items"
            onClick={() => setShowHidden(!showHidden)}
          >
            {showHidden ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button className="p-1 rounded-md hover:bg-gray-200" title="Filter">
            <Filter size={14} />
          </button>
          {/* Card Size Control */}
          <div className="flex items-center bg-gray-100 rounded-md" style={{ width: '60px' }}>
            <button 
              className={`flex flex-col justify-center items-center p-0.5 rounded-md hover:bg-gray-200 text-xs font-medium w-1/3 h-6 ${cardSize === 'small' ? 'bg-blue-100 text-blue-700' : ''}`}
              title="Small Card Size (1 line)"
              onClick={() => setCardSize('small')}
            >
              <div className="w-4 h-0.5 bg-current"></div>
            </button>
            <button 
              className={`flex flex-col justify-center items-center p-0.5 rounded-md hover:bg-gray-200 text-xs font-medium w-1/3 h-6 ${cardSize === 'medium' ? 'bg-blue-100 text-blue-700' : ''}`}
              title="Medium Card Size (3 lines)"
              onClick={() => setCardSize('medium')}
            >
              <div className="w-4 h-0.5 bg-current mb-1"></div>
              <div className="w-4 h-0.5 bg-current"></div>
            </button>
            <button 
              className={`flex flex-col justify-center items-center p-0.5 rounded-md hover:bg-gray-200 text-xs font-medium w-1/3 h-6 ${cardSize === 'large' ? 'bg-blue-100 text-blue-700' : ''}`}
              title="Large Card Size (6 lines)"
              onClick={() => setCardSize('large')}
            >
              <div className="w-4 h-0.5 bg-current mb-1"></div>
              <div className="w-4 h-0.5 bg-current"></div>
              <div className="w-4 h-0.5 bg-current mt-1"></div>
            </button>
          </div>
          <div className="flex items-center bg-gray-100 rounded-md px-0.5">
            <button 
              className="p-0.5 rounded-md hover:bg-gray-200" 
              title="Decrease Columns"
              onClick={() => setGridColumns(Math.max(1, gridColumns - 1))}
              disabled={gridColumns <= 1}
            >
              <Columns size={14} />-
            </button>
            <span className="mx-1 text-gray-700 font-medium text-xs">{gridColumns}</span>
            <button 
              className="p-0.5 rounded-md hover:bg-gray-200" 
              title="Increase Columns"
              onClick={() => setGridColumns(Math.min(maxColumns, gridColumns + 1))}
              disabled={gridColumns >= maxColumns}
            >
              <Columns size={14} />+
            </button>
          </div>
        </div>
      </div>
      
      <div className="overflow-auto flex-1 p-[5px] w-full" ref={gridContainerRef}>
        <div className="grid gap-[5px] w-full h-full" 
          style={{ 
            gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
            gridAutoRows: cardSize === 'small' ? '2rem' : cardSize === 'large' ? '8rem' : '4rem',
            minHeight: 'calc(100% - 10px)'
          }}
        >
          {/* Item cards with badges */}
          {searchFilteredItems.map((item, index) => {
            // Find the original index of this item in the full items array
            const originalIndex = items.findIndex(i => i.refId === item.refId);
            const isBeingDragged = originalIndex === draggedItemIndex;
            
            return (
              <div 
                key={item.refId} 
                className="relative group h-full w-full"
                ref={el => itemRefs.current[item.refId] = el}
                draggable="true"
                onDragStart={(e) => handleDragStart(e, originalIndex, e.currentTarget)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e)}
                onDragEnter={(e) => handleDragEnter(e, originalIndex)}
                onDragLeave={(e) => handleDragLeave(e, originalIndex)}
                onDrop={(e) => handleDrop(e, originalIndex)}
              >
                {/* Drag handle indicator */}
                <div className="absolute -left-6 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 z-10">
                  <GripVertical size={18} className="text-gray-500" />
                </div>
                
                {/* Item card - Added cursor-grab to indicate draggability */}
                <div 
                  className={`${isBeingDragged ? 'opacity-30' : ''} cursor-grab active:cursor-grabbing group-hover:bg-blue-50 transition-colors duration-150 relative`}
                >
                  {/* Drop indicator - before (left side) */}
                  {dropPosition && 
                   dropPosition.index === originalIndex && 
                   dropPosition.position === 'before' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500 z-10 -ml-3 rounded-md shadow-lg flex flex-col items-center justify-center overflow-visible">
                      <div className="absolute w-5 h-5 bg-blue-500 rounded-full top-1/4 transform -translate-x-2 shadow-lg"></div>
                      <div className="absolute w-5 h-5 bg-blue-500 rounded-full bottom-1/4 transform -translate-x-2 shadow-lg"></div>
                    </div>
                  )}
                  
                  <SurveyItemCard 
                    item={item} 
                    isSelected={item.refId === selectedItemId}
                    onSelect={() => {
                      isSelectingInGridRef.current = true;
                      
                      // Set a global flag that the detail view can check
                      window.isSelectingFromGrid = true;
                      
                      setSelectedItemId(item.refId);
                    }}
                    onToggleVisibility={() => toggleItemVisibility(item.refId)}
                    size={cardSize}
                  />
                  
                  {/* Drop indicator - after (right side) */}
                  {dropPosition && 
                   dropPosition.index === originalIndex && 
                   dropPosition.position === 'after' && (
                    <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-blue-500 z-10 -mr-3 rounded-md shadow-lg flex flex-col items-center justify-center overflow-visible">
                      <div className="absolute w-5 h-5 bg-blue-500 rounded-full top-1/4 transform translate-x-2 shadow-lg"></div>
                      <div className="absolute w-5 h-5 bg-blue-500 rounded-full bottom-1/4 transform translate-x-2 shadow-lg"></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Add button */}
          <button className="border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition flex flex-col items-center justify-center w-full h-full">
            <Plus size={14} className="text-gray-500 mb-1" />
            <span className="text-[10px] text-gray-500">Add Item</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyItemGrid;